"use server";

import { revalidatePath } from "next/cache";
import { prisma } from "@/lib/db";
import { requireAdmin } from "@/lib/admin-guard";

function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

async function logAudit(
  user: { id: string; name: string; email: string },
  action: string,
  target: string,
  meta: Record<string, unknown>,
) {
  await prisma.auditLog.create({
    data: {
      actorId: user.id,
      actorName: user.name,
      actorEmail: user.email,
      source: "admin",
      action,
      target,
      meta: meta as never,
    },
  });
}

export async function createTagAction(formData: FormData) {
  const user = await requireAdmin();
  const name = String(formData.get("name") ?? "").trim();
  if (!name) return { ok: false as const, error: "Thiếu tên." };
  const slug = String(formData.get("slug") ?? "").trim() || slugify(name);

  const exist = await prisma.tag.findUnique({ where: { slug } });
  if (exist) return { ok: false as const, error: `Slug "${slug}" đã tồn tại.` };

  const t = await prisma.tag.create({ data: { name, slug } });
  await logAudit(user, "tag.create", t.id, { name, slug });
  revalidatePath("/admin/tags");
  return { ok: true as const, id: t.id };
}

export async function updateTagAction(id: string, formData: FormData) {
  const user = await requireAdmin();
  const name = String(formData.get("name") ?? "").trim();
  const slug = String(formData.get("slug") ?? "").trim();
  if (!name || !slug) return { ok: false as const, error: "Thiếu tên hoặc slug." };

  const conflict = await prisma.tag.findFirst({
    where: { slug, NOT: { id } },
    select: { id: true },
  });
  if (conflict) return { ok: false as const, error: `Slug "${slug}" trùng.` };

  await prisma.tag.update({ where: { id }, data: { name, slug } });
  await logAudit(user, "tag.update", id, { name, slug });
  revalidatePath("/admin/tags");
  revalidatePath(`/tag/${slug}`);
  return { ok: true as const };
}

export async function deleteTagAction(id: string) {
  const user = await requireAdmin();
  const t = await prisma.tag.findUnique({
    where: { id },
    select: { name: true, slug: true, _count: { select: { articles: true } } },
  });
  if (!t) return { ok: false as const, error: "Không tìm thấy." };

  // Tag has CASCADE on ArticleTag pivot, safe to delete
  await prisma.tag.delete({ where: { id } });
  await logAudit(user, "tag.delete", id, t);
  revalidatePath("/admin/tags");
  return { ok: true as const };
}
